import { ToastrService } from 'ngx-toastr';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { SnIncidentMedia } from '@lib/api/entities';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { LprIntegrationType } from '@lib/data-dog-rum/custom-action-map';

import {
  FsLprFleetRead,
  FsLprRawFleetRead,
  FsLprFleetResults,
  IFsLprFleetParameters,
  FsLprAxonVIUser,
  FsLprAxonVIFilterSuggestion,
  FsLprAxonVIFilterSuggestionResults,
} from '../api';
import {
  extractLprErrorCode,
  extractLprRawError,
  parseLprApiError,
} from '../utils/lpr-error-utils';

/**
 * @deprecated This service is legacy and will be removed in a future release.
 *
 * DO NOT USE THIS SERVICE IN NEW CODE.
 *
 * This service communicates with the old fleet_ps integration endpoints and is only kept
 * for backwards compatibility during the transition period. The fallback to fleet_ps when
 * Axon VI is disabled has been removed. Use AxonVILprService instead.
 *
 * Scheduled for removal: TBD
 */
@Injectable({
  providedIn: 'root',
})
export class FleetLprService {
  private readonly _integrationType: LprIntegrationType = 'fleet_ps';

  // API endpoints
  private readonly PRIMARY_ENDPOINT = '/api/integrations/axon/fleet_ps/plate_search/axon_connect/';
  private readonly FALLBACK_ENDPOINT = '/api/integrations/axon/fleet_ps/plate_search/';

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _metrics: FsDataDogRumService,
  ) {}

  /**
   * Fetches license plate data from the Fleet API
   * Uses the axon_connect endpoint first, with fallback to the original endpoint if it fails
   *
   * @param params Query parameters for the search (size, page, search_reason, etc.)
   * @returns Observable of the search results with metadata
   */
  public getFleetPlates(params: Partial<IFsLprFleetParameters>): Observable<FsLprFleetResults> {
    this.processGetFleetPlatesParams(params);

    const httpParams = new HttpParams({ fromObject: { ...params } });
    const startTime = Date.now();

    return this._httpClient
      .get<FsLprFleetResults>(this.PRIMARY_ENDPOINT, {
        params: httpParams,
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const data = response.body;
          if (Array.isArray(data.data)) {
            data.data = data.data.map((result) => new FsLprFleetRead(result));
          }

          this._metrics.addAction('lpr_search', {
            status: response.status,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
          });

          return {
            ...data,
            status: response.status,
          };
        }),
        catchError((primaryError) => {
          // Log the primary endpoint error
          this._metrics.addAction('lpr_search_error', {
            status: primaryError?.status || 500,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
            raw_error: extractLprRawError(primaryError),
            error_code: extractLprErrorCode(primaryError),
            error_message: parseLprApiError(primaryError),
          });

          // Fall back to the legacy endpoint
          return this._getFromFallbackEndpoint(httpParams, startTime);
        }),
      );
  }

  /**
   * Attempts to fetch license plate data from the fallback endpoint
   *
   * @param httpParams HTTP parameters for the request
   * @param startTime The time when the original request started (for metrics)
   * @returns Observable of the search results with metadata
   */
  private _getFromFallbackEndpoint(
    httpParams: HttpParams,
    startTime: number,
  ): Observable<FsLprFleetResults> {
    // TODO: FWX-1292 - Remove the fallback endpoint
    // from Fleet Service once all the customers are using the new endpoint
    return this._httpClient
      .get<FsLprFleetResults>(this.FALLBACK_ENDPOINT, {
        params: httpParams,
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const data = response.body;
          if (Array.isArray(data.data)) {
            data.data = data.data.map((result) => new FsLprFleetRead(result));
          }

          this._metrics.addAction('lpr_search', {
            status: response.status,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
          });

          return {
            ...data,
            status: response.status,
          };
        }),
        catchError((fallbackError) => {
          // Log the fallback endpoint error
          this._metrics.addAction('lpr_search_error', {
            status: fallbackError?.status || 500,
            duration_ms: Date.now() - startTime,
            integration: this._integrationType,
            raw_error: extractLprRawError(fallbackError),
            error_code: extractLprErrorCode(fallbackError),
            error_message: parseLprApiError(fallbackError),
          });

          // Return the error to the caller
          return throwError(fallbackError);
        }),
      );
  }

  public addMediaToVault(id: string, data: Object): Observable<SnIncidentMedia> {
    return this._httpClient.post(`/api/vaults/${id}/media/`, data).pipe(
      map((response) => new SnIncidentMedia(response)),
      tap({
        next: () => this._toastrService.success('Report was sent successfully'),
        error: () => this._toastrService.error('Unable to send report. Please try again later'),
      }),
    );
  }

  public getRead({
    readId,
    timestamp,
    searchReason,
  }: {
    readId: string;
    timestamp: number;
    searchReason: string;
  }): Observable<FsLprFleetRead> {
    return this._httpClient
      .get<{ data: any }>(`${this.PRIMARY_ENDPOINT}${readId}/`, {
        params: {
          timestampMillis: timestamp,
          searchReason,
        },
      })
      .pipe(
        map((response) => {
          // Create a raw LPR read from the response data
          const rawRead = new FsLprRawFleetRead(response.data);
          // Convert to the standard FsLprFleetRead format
          return rawRead.toFsLprFleetRead();
        }),
      );
  }

  public getUsers(): Observable<FsLprAxonVIUser[]> {
    return this._httpClient
      .get<FsLprAxonVIUser[]>(`${this.PRIMARY_ENDPOINT}users/`, { observe: 'response' })
      .pipe(
        map((response) => {
          if (Array.isArray(response.body)) {
            return response.body.map((user) => new FsLprAxonVIUser(user));
          }
          return [];
        }),
        catchError((error) => {
          console.error('Failed to fetch users:', error);
          return of([]);
        }),
      );
  }

  public getCameraNameSuggestions(searchTerm: string): Observable<FsLprAxonVIFilterSuggestion[]> {
    return this._httpClient
      .get<FsLprAxonVIFilterSuggestionResults>(`${this.PRIMARY_ENDPOINT}filters/suggestions/`, {
        params: {
          fieldName: 'camera.name',
          searchTerm,
          limit: 100,
        },
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const data = response.body?.data;
          if (Array.isArray(data)) {
            return data.map((item) => new FsLprAxonVIFilterSuggestion(item));
          }
          return [];
        }),
        catchError((error) => {
          console.error('Failed to fetch camera name suggestions:', error);
          return of([]);
        }),
      );
  }

  private processGetFleetPlatesParams(params: Partial<IFsLprFleetParameters>): void {
    if (!params.includeFacets) {
      delete params.includeFacets;
    }

    if (!params.cameraVendors || !params.cameraVendors.length) {
      delete params.cameraVendors;
    }

    if (!params.cameraModels || !params.cameraModels.length) {
      delete params.cameraModels;
    }
  }
}
